import React from 'react'
import styled, { css } from 'styled-components'
import { connect } from 'react-redux'
import { isQcsBotPayload } from 'constants/qcs'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import * as S from '../CarecoAdminDashboard/CarecoAdminStyle'
import { UserIcon, RightIcon, ManagerIcon, CarerIcon, AdminIcon, ServiceUserIcon } from 'uiKit/icons/Icons'
import LabelWithTooltip from 'uiKit/LabelWithTooltip/LabelWithTooltip'
import { isProfessionalsNavAllowed, isServiceUsersNavAllowed } from 'tabs/home/api/usageLimits'

type OverviewData = {
  activeUsers?: number
  archivedUsers?: number
  activeManagers?: number
  archivedManagers?: number
  activeCarers?: number
  archivedCarers?: number
  activeAdmins?: number
  archivedAdmins?: number
  totalUsers?: number
  totalManagers?: number
  totalCarers?: number
  totalAdmins?: number
  activeServiceUsers?: number
  archivedServiceUsers?: number
  totalServiceUsers?: number
} | null

interface OverviewProps extends RouteComponentProps {
  data?: OverviewData
  showUserStatuses?: boolean
  activeBot?: any
}

const OverviewCards = styled(S.OverviewCards)<{ $isQcs: boolean }>`
  ${({ $isQcs }) => $isQcs && css`
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    gap: 16px;

    & > div:first-child { flex: 4 1 900px; min-width: 0; }
    & > div + div { flex: 1 1 220px; min-width: 0; }
  `}
`

const OverviewCard = styled(S.Card)<{ $isQcs: boolean }>`
  ${({ $isQcs }) => $isQcs && css`
    box-sizing: border-box;
    padding: 20px;

    ${S.HeaderRow} { margin-bottom: 34px; }
    ${S.HeaderLeft} { min-width: 0; flex-wrap: wrap; }
    ${S.HeaderLeft} > div:last-child { margin-left: 12px; }
    ${S.HeaderRow} > svg, ${S.IconCircle} { flex-shrink: 0; }
    ${S.StatsGrid} ${S.IconCircle} { width: 36px; height: 36px; }
    ${S.StatsGrid} {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
    }
    ${S.StatItem}:nth-child(n) {
      display: grid;
      grid-template-columns: 36px minmax(max-content, 1fr) auto;
      gap: 8px;
      flex: 1 1 auto;
      min-width: max-content;
      padding-left: 0;
      border-left: none;
    }
    ${S.StatContent} { min-width: 0; }
    ${S.StatLabel} { white-space: nowrap; }
    ${S.StatLabel} > span { white-space: nowrap; }
    ${S.StatItem} + ${S.StatItem} {
      padding-left: 16px;
      border-left: 1px solid var(--careco-divider);
    }
    ${S.StatItem}:only-child {
      flex: 0 1 auto;
      grid-template-columns: 36px max-content auto;
      column-gap: 16px;
    }
  `}
`

const ServiceUsersCard = styled(S.Card)<{ $isQcs: boolean }>`
  ${({ $isQcs }) => $isQcs && css`
    box-sizing: border-box;
    padding: 20px;
    ${S.HeaderRow} { margin-bottom: 34px; }
    ${S.HeaderLeft} { gap: 12px; flex-wrap: nowrap; }
    ${S.IconCircle} { flex-shrink: 0; }
    ${S.StatsGrid} { grid-template-columns: minmax(0, 1fr); }
  `}
`

const StatusBadge = styled.div`
  display: flex;
  flex-direction: column;
  width: max-content;
  min-width: max-content;
  flex-shrink: 0;
  margin-left: auto;
  color: var(--color-text-secondary);
  font-size: 14px;
  line-height: 24px;
  white-space: nowrap;

  & > span {
    display: grid;
    grid-template-columns: 8px minmax(8ch, 1fr) var(--status-count-width);
    align-items: center;
    column-gap: 12px;
  }
  & > span + span { border-top: 1px solid #e5e9e9; }
  & > span::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #2e7d32;
  }
  & > span + span::before { background: #b77a13; }
  strong {
    text-align: right;
    font-variant-numeric: tabular-nums;
    color: var(--color-text-black);
  }
`

const UserStatuses: React.FC<{ active?: number; archived?: number }> = ({ active, archived }) => (
  <StatusBadge style={{
    '--status-count-width': `${Math.max(3, String(active ?? 0).length, String(archived ?? 0).length)}ch`,
  } as React.CSSProperties}>
    <span>Active <strong>{active ?? 0}</strong></span>
    <span>Archived <strong>{archived ?? 0}</strong></span>
  </StatusBadge>
)

export const Overview: React.FC<OverviewProps> = ({ history, match, data, activeBot, showUserStatuses = false }) => {
  const isQcs = isQcsBotPayload(activeBot)
  const showArchiveCounts = isQcs && showUserStatuses
  const userTotal = (total?: number, active?: number, archived?: number) => {
    if (total != null) return total
    if (showArchiveCounts && archived != null) return (active ?? 0) + archived
    return active ?? 0
  }
  const totals = {
    users: userTotal(data?.totalUsers, data?.activeUsers, data?.archivedUsers),
    managers: userTotal(data?.totalManagers, data?.activeManagers, data?.archivedManagers),
    carers: userTotal(data?.totalCarers, data?.activeCarers, data?.archivedCarers),
    admins: userTotal(data?.totalAdmins, data?.activeAdmins, data?.archivedAdmins),
    serviceUsers: data?.totalServiceUsers ?? 0,
  }
  const showManagersAndCarers = !isQcs || isProfessionalsNavAllowed(activeBot)
  // Supervision off (and never used) → only admins are listed, so the header counts admins.
  const totalUsersCount = showManagersAndCarers ? totals.users : totals.admins
  const showServiceUsers = data?.totalServiceUsers !== null && (!isQcs || isServiceUsersNavAllowed(activeBot))

  const handleNavigation = () => {
    const baseUrl = (match as any)?.url || ''
    history.push(`${baseUrl}/total-users`)
  }

  return (
    <S.Container>
      <S.SectionTitle>Overview</S.SectionTitle>
      <OverviewCards $isQcs={isQcs}>
      <OverviewCard $isQcs={isQcs}>
        <S.HeaderRow>
          <S.HeaderLeft>
            <S.IconCircle>
              <UserIcon />
            </S.IconCircle>
            <S.StatContent>
            <S.CardSubtitle>
              Total users: {totalUsersCount}
            </S.CardSubtitle>
            </S.StatContent>
            <LabelWithTooltip
              text=""
              tooltipId="overview-total-users"

              tooltipText="Total number of unique users registered across your organisation (all roles combined)"
            />
            {showArchiveCounts && (showManagersAndCarers ? data?.archivedUsers : data?.archivedAdmins) != null && (
              <UserStatuses
                active={showManagersAndCarers ? data?.activeUsers : data?.activeAdmins}
                archived={showManagersAndCarers ? data?.archivedUsers : data?.archivedAdmins}
              />
            )}
          </S.HeaderLeft>
          <RightIcon onClick={handleNavigation} />
        </S.HeaderRow>

        <S.StatsGrid>
          {showManagersAndCarers && (
            <>
              <S.StatItem>
                <S.IconCircle>
                  <ManagerIcon />
                </S.IconCircle>
                <S.StatContent>
                  <S.StatLabel>
                    <LabelWithTooltip
                      text="Total managers"
                      tooltipId="overview-total-managers"
                      marginLeft={0}
                      tooltipText="Number of users responsible for supervising carers and overseeing care delivery"
                    />
                  </S.StatLabel>
                  <S.CardSubtitle>{totals.managers}</S.CardSubtitle>
                </S.StatContent>
                  {showArchiveCounts && data?.archivedManagers != null && (
                    <UserStatuses active={data?.activeManagers} archived={data?.archivedManagers} />
                  )}
              </S.StatItem>

              <S.StatItem>
                <S.IconCircle>
                  <CarerIcon />
                </S.IconCircle>
                <S.StatContent>
                  <S.StatLabel>
                    <LabelWithTooltip
                      text="Total carers"
                      tooltipId="overview-total-carers"
                      marginLeft={0}
                      tooltipText="Number of people who use the app but don’t manage or supervise others"
                    />
                  </S.StatLabel>
                  <S.CardSubtitle>{totals.carers}</S.CardSubtitle>
                </S.StatContent>
                  {showArchiveCounts && data?.archivedCarers != null && (
                    <UserStatuses active={data?.activeCarers} archived={data?.archivedCarers} />
                  )}
              </S.StatItem>
            </>
          )}

          {data?.totalAdmins !== null && (
          <S.StatItem>
            <S.IconCircle>
              <AdminIcon />
            </S.IconCircle>
            <S.StatContent>
              <S.StatLabel>
                <LabelWithTooltip
                  text="Total admins"
                  tooltipId="overview-total-admins"
                  marginLeft={0}
                  tooltipText="Number of users managing system access, settings, and organisation configuration"
                />
              </S.StatLabel>
              <S.CardSubtitle>{totals.admins}</S.CardSubtitle>
            </S.StatContent>
                  {showArchiveCounts && data?.archivedAdmins != null && (
                    <UserStatuses active={data?.activeAdmins} archived={data?.archivedAdmins} />
                  )}
          </S.StatItem>
          )}

        </S.StatsGrid>
      </OverviewCard>

      {showServiceUsers && (
      <ServiceUsersCard $isQcs={isQcs}>
        <S.HeaderRow>
          <S.HeaderLeft>
            <S.CardSubtitle>Service users</S.CardSubtitle>
            <LabelWithTooltip
              text=""
              tooltipId="overview-service-users"
              constrainToViewport
              tooltipText="Receives support and care from the organisation"
            />
          </S.HeaderLeft>
        </S.HeaderRow>

        <S.StatsGrid>
          <S.StatItem>
            <S.IconCircle><ServiceUserIcon /></S.IconCircle>
            <S.StatContent>
              <S.StatLabel>Total service users</S.StatLabel>
              <S.CardSubtitle>{totals.serviceUsers}</S.CardSubtitle>
            </S.StatContent>
          </S.StatItem>
        </S.StatsGrid>
      </ServiceUsersCard>
      )}
      </OverviewCards>
    </S.Container>
  )
}

const mapStateToProps = (state: { activeBot?: any }) => ({
  activeBot: state.activeBot,
})

export default connect(mapStateToProps)(withRouter(Overview))
